import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { ViewModule, CalendarMonth } from '@mui/icons-material';
import { Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router';
import CardEvent from '../../components/CardEvent/CardEvent';
import { TranslucentContainer } from '../../../../../components';
import * as EventActions from '../../../../../core/events/action';
import { formatDate, getFormattedTime } from '../../../../../utils';
import { APP_ROUTES } from '../../../../../constants/app-routes';
import CardButtons from '../../components/CardButtons';
import VenueCalendarView from './VenueCalendarView';
import './ManageEvents.scss';

function ManageEvents() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const CustomizedSwal = withReactContent(Swal);
  const { eventList } = useSelector((store) => store.events);
  const { currentVenue } = useSelector((store) => store.venues);

  // Load view mode from localStorage, default to 'grid' if not set
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('venueManageEventsViewMode');
    return savedViewMode || 'grid';
  });

  useEffect(() => {
    if (currentVenue) dispatch(EventActions.getEventsByVenue(currentVenue.id));
  }, [currentVenue, dispatch]);

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('venueManageEventsViewMode', newMode);
    }
  };

  const getFormatDate = (date) => moment(date).format('LL');

  const getFormatHours = (initHour, endHour) => {
    const A = new Date();
    A.setHours(...initHour.split(':'));
    const B = new Date();
    B.setHours(...endHour.split(':'));
    return `${moment(A).format('HH:mm')} - ${moment(B).format('HH:mm')}`;
  };

  const getModalFailed = useCallback(
    (name, description) => {
      CustomizedSwal.fire({
        title: <strong>{name}</strong>,
        text: description,
        icon: 'error',
      });
    },
    [CustomizedSwal],
  );
  const onHandlerActions = (event, buttonType, eventId) => {
    const eventSearch = eventList.find((i) => i.id === eventId);
    switch (buttonType) {
      // Update Event
      case 'primary': {
        dispatch(EventActions.resetUpdateEventForm());
        const updatePage = APP_ROUTES.UPDATE_EVENT.replace(':id', eventId);
        navigate(updatePage, { state: { event: eventSearch } });
        break;
      }
      // Cancel Event
      case 'secondary': {
        const { event_date: eventDate, hour_start_event: hourStartEvent } =
          eventSearch.parameters_event;
        const compareDate = moment(`${moment(eventDate).format('YYYY/MM/DD')} ${hourStartEvent}`);
        const currentDate = moment();
        const diffHours = compareDate.diff(currentDate, 'hours');
        console.log({ diffHours, currentDate, compareDate });

        if (diffHours > 48) dispatch(EventActions.onCancelEvent(eventId));
        else if (diffHours < 0)
          getModalFailed('Event cancellation failed', 'You can not cancel a past event');
        else
          getModalFailed(
            'Event cancellation failed',
            'It is not possible to cancel this event 48 hours before the event',
          );
        break;
      }
      default:
        break;
    }
  };

  // Render Grid View
  const renderGridView = () => (
    <Grid container marginTop={5} justifyContent="center">
      <TranslucentContainer>
        <Grid container justifyContent="start" marginTop={8}>
          {eventList?.length === 0 && (
            <Grid container justifyContent="center">
              <Typography variant="h5" textAlign="center">
                There are no current registered events
              </Typography>
            </Grid>
          )}
          {eventList
            ?.sort(
              (a, b) =>
                moment(b.parameters_event?.event_date) - moment(a.parameters_event?.event_date),
            )
            .map((e) => (
              <Grid key={uuidv4()} item sm={5} lg={3} marginBottom={8} paddingLeft={8}>
                <CardEvent
                  status={e.status}
                  title={e.name}
                  eventId={e.id}
                  image={e.photo}
                  eventDate={formatDate(e.parameters_event?.event_date)}
                  startEvent={e.parameters_event ? getFormattedTime(e) : ''}>
                  <Grid marginTop={1}>
                    <CardButtons
                      eventId={e.id}
                      primaryButton="Edit"
                      secondaryButton="Cancel"
                      onHandleClick={onHandlerActions}
                    />
                  </Grid>
                </CardEvent>
              </Grid>
            ))}
        </Grid>
      </TranslucentContainer>
    </Grid>
  );

  return (
    <>
      {/* View Toggle - Only show when events exist */}
      {eventList?.length > 0 && (
        <Grid container justifyContent="center" marginTop={3}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            sx={{
              backgroundColor: 'white',
              '& .MuiToggleButton-root': {
                color: '#666',
                borderColor: '#EE7B57',
                '&.Mui-selected': {
                  backgroundColor: '#EE7B57',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#d66a47',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(238, 123, 87, 0.1)',
                },
              },
            }}>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule sx={{ marginRight: 1 }} />
              Grid View
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarMonth sx={{ marginRight: 1 }} />
              Calendar View
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}

      {/* Render content based on view mode */}
      {viewMode === 'grid' ? renderGridView() : <VenueCalendarView events={eventList || []} />}
    </>
  );
}

export default ManageEvents;
